import React, { useEffect, useState, useRef } from 'react';
import './App.css';

// Decode interview token to extract payload
function decodeInterviewToken(token: string | null) {
  if (!token) return null;
  try {
    const [payloadBase64] = token.split('.');
    const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    return {
      candidateId: payload.candidate_id,
      email: payload.email,
      name: payload.name,
      shareKey: payload.shareKey,
      exp: payload.exp,
    };
  } catch (error) {
    console.error('Failed to decode interview token:', error);
    return null;
  }
}
export default function App() {
  const [interviewToken, setInterviewToken] = useState<string | null>(null);
  const [candidateName, setCandidateName] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'live' | 'ended'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [linkClicks, setLinkClicks] = useState<number>(0);
  const [linkBlocked, setLinkBlocked] = useState<boolean>(false);
  const [noToken, setNoToken] = useState<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const vapiRef = useRef<any>(null);
  const hadActiveCallRef = useRef<boolean>(false);
  const startClaimedRef = useRef<boolean>(false);
  const [showRating, setShowRating] = useState<boolean>(false);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);
  const [ratingLoading, setRatingLoading] = useState<boolean>(false);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawSearch = window.location.search || '';
    console.log('Location href:', window.location.href);
    console.log('Raw search:', rawSearch);
    console.log('URLSearchParams:', params.toString());

    const token = params.get('interviewToken');
    const decodedToken = token ? decodeURIComponent(token) : null;

    if (!token) {
      setNoToken(true);
      return;
    }
    setNoToken(false);

    const decodedPayload = decodeInterviewToken(token);
    if (decodedPayload) {
      setCandidateName(decodedPayload.name);
    }

    setInterviewToken(decodedToken);

    // removed the previous /api/link-click call — counting now occurs on call start

    const decoded = decodeInterviewToken(token);
    if (decoded) {
      setCandidateName(decoded.name);
      console.log('Decoded token:', decoded);
    }

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // start/stop timer when status changes
    if (status === 'live') {
      timerRef.current = window.setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [status]);

  const endCall = async () => {
    try {
      if (vapiRef.current) {
        if (typeof vapiRef.current.end === 'function') {
          await vapiRef.current.end();
        } else if (typeof vapiRef.current.stop === 'function') {
          await vapiRef.current.stop();
        } else if (vapiRef.current.activeCall && typeof vapiRef.current.activeCall.hangup === 'function') {
          await vapiRef.current.activeCall.hangup();
        } else if (vapiRef.current.currentCall && typeof vapiRef.current.currentCall.hangup === 'function') {
          await vapiRef.current.currentCall.hangup();
        } else if (vapiRef.current.calls && typeof vapiRef.current.calls.end === 'function') {
          // best-effort: end the first known call
          const keys = Object.keys(vapiRef.current.calls || {});
          if (keys.length) {
            await vapiRef.current.calls.end(keys[0]);
          }
        } else {
          console.warn('No supported shutdown method found on VAPI instance');
        }
      }
    } catch (e) {
      console.error('Error while attempting to end VAPI call', e);
    } finally {
      setStatus('idle');
      setSeconds(0);
      setIsSpeaking(false);
      setMicOn(true);
    }
  };

  useEffect(() => {
    // expose helpers for debugging in the browser console
    const w = window as any;
    w.__vapiRef = vapiRef;
    w.startVapi = startVapi;
    return () => {
      delete w.__vapiRef;
      delete w.startVapi;
    };
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_VAPI_AUTO_START === 'true') {
      // auto-start VAPI if configured in env
      startVapi();
    }
  }, []);

  const toggleMic = () => {
    const newMicState = !micOn;
    setMicOn(newMicState);

    // Actually mute/unmute the VAPI microphone
    if (vapiRef.current && typeof vapiRef.current.setMuted === 'function') {
      vapiRef.current.setMuted(!newMicState);
    }
  };

  const startVapi = async () => {
    if (linkBlocked) {
      console.warn('This interview link has been opened too many times and is blocked.');
      return;
    }

    if (!interviewToken) {
      console.warn('Invalid interview link');
      return;
    }

    setStatus('connecting');
    setSeconds(0);

    // Claim a start slot on the server before starting VAPI. If the server reports blocked,
    // do not start VAPI at all.
    try {
      const base = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
      const url = `${base.replace(/\/$/, '')}/api/call-start`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: interviewToken, candidateName: candidateName || null }),
      });
      const data = await resp.json().catch(() => ({}));
      console.log('pre-call-start response', resp.status, data);
      if (data.blocked) {
        console.warn('Call prevented: link is blocked by server');
        setLinkBlocked(true);
        setStatus('idle');
        return;
      }
      if (typeof data.count === 'number') setLinkClicks(data.count);
      // mark that we've already reported/claimed a start so the VAPI event handler won't double-report
      startClaimedRef.current = true;
    } catch (e) {
      console.warn('Failed to claim call-start; proceeding to start VAPI (best-effort)', e);
    }
    const params = new URLSearchParams(window.location.search);

    const assistantId =
      params.get('assistantId') ||
      params.get('assistant_id') ||
      process.env.REACT_APP_VAPI_ASSISTANT_ID;

    const key = process.env.REACT_APP_VAPI_KEY;

    if (!key) {
      console.warn('VAPI API key is missing. Please configure REACT_APP_VAPI_KEY in your .env file.');
      setStatus('idle');
      return;
    }

    if (!assistantId) {
      console.warn('VAPI Assistant ID is missing. Please configure REACT_APP_VAPI_ASSISTANT_ID in your .env file or pass it as a URL parameter.');
      setStatus('idle');
      return;
    }

    const container = document.getElementById('vapiContainer');
    console.log('startVapi() called', { assistantId, containerPresent: !!container });

    try {
      const mod = await import('@vapi-ai/web');
      const Vapi = (mod && (mod.default || mod));

      // Clean up any existing instance
      if (vapiRef.current) {
        try {
          await vapiRef.current.stop?.();
        } catch (e) {
          console.warn('Failed to stop existing VAPI instance', e);
        }
      }

      vapiRef.current = new Vapi(key);

      vapiRef.current.on?.('call-start', async (payload: any) => {
        console.log('VAPI call-start event fired', payload);
        // If we already claimed a start before starting VAPI, don't report again to the server.
        if (startClaimedRef.current) {
          startClaimedRef.current = false;
          setStatus('live');
          hadActiveCallRef.current = true;
          return;
        }

        // Otherwise, attempt to report the start now (backward compatibility)
        try {
          const base = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
          const url = `${base.replace(/\/$/, '')}/api/call-start`;
          const resp = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: interviewToken, candidateName: candidateName || null }),
          });
          const data = await resp.json().catch(() => ({}));
          console.log('call-start response', resp.status, data);
          if (typeof data.count === 'number') setLinkClicks(data.count);
          if (data.blocked) {
            setLinkBlocked(true);
            console.warn('This interview link has been opened/started too many times and is now blocked.');
            try {
              await endCall();
            } catch (e) {
              console.warn('Failed to end blocked call', e);
              setStatus('idle');
              setSeconds(0);
            }
            return; // do not mark as live
          }
        } catch (e) {
          console.warn('Failed to report call-start', e);
        }

        setStatus('live');
        hadActiveCallRef.current = true;
      });

      vapiRef.current.on?.('call-end', () => {
        console.log('VAPI call-end event fired');
        // mark ended
        setStatus('ended');
        // stop the timer immediately
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setSeconds(0);
        setIsSpeaking(false);
        setMicOn(true);

        // Only show rating overlay when a real call started, and the link is valid (not blocked)
        if (hadActiveCallRef.current && !linkBlocked && !noToken) {
          // reset rating UI so previous values are not shown
          setSelectedRating(null);
          setHoverRating(null);
          setRatingSubmitted(false);
          setRatingLoading(false);
          setRatingError(null);
          setFeedbackText('');
          setShowRating(true);
        } else {
          // ensure rating UI is hidden so the warning/invalid token UI can display
          setShowRating(false);
        }
        // reset the call-start flag for the next session
        hadActiveCallRef.current = false;
      });

      vapiRef.current.on?.('error', (error: any) => {
        console.error('VAPI error:', error);
        setStatus('idle');
        setSeconds(0);
      });

      vapiRef.current.on?.('speech-start', () => {
        console.log('Speech started');
        setIsSpeaking(true);
      });

      vapiRef.current.on?.('speech-end', () => {
        console.log('Speech ended');
        setIsSpeaking(false);
      });

      vapiRef.current.on?.('message', (msg: any) => {
        if (msg.type === 'transcript') {
          console.log(msg.role, msg.transcript);
        }
      });

      if (container && typeof vapiRef.current.mount === 'function') {
        try {
          vapiRef.current.mount(container);
        } catch (err) {
          console.warn('VAPI mount failed', err);
        }
      }

      // Build start options with interview token
      const startOptions: any = {
        metadata: {
          interviewToken,
          source: 'qweb-interview'
        },

        variableValues: {
          candidateName: candidateName || 'Unknown',
          interviewToken
        }
      };

      console.log('Starting VAPI with candidate:', candidateName);
      console.log('Starting VAPI with options:', startOptions);

      // ✅ START CALL - simplified to avoid 400 errors
      await vapiRef.current.start(assistantId, startOptions);


      console.log('VAPI started successfully');
    } catch (err) {
      console.error('Failed to start VAPI', err);
      setStatus('idle');
      setSeconds(0);
      // Log user-friendly error to console instead of alert
      console.error('Failed to start the interview. Please check your connection and try again.');
    }
  };

  const toggleSpeaker = () => {
    setSpeakerOn((v) => !v);
  };

  const formatTime = (s: number) => {
    const mm = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const submitRating = async (rating: number) => {
    setRatingError(null);
    if (rating == null) {
      const msg = 'Please select a rating before submitting.';
      console.warn(msg);
      setRatingError(msg);
      return;
    }
    if (!interviewToken) {
      const msg = 'No interview token; cannot submit rating';
      console.warn(msg);
      setRatingError(msg);
      return;
    }

    // immediate UI feedback
    setSelectedRating(rating);
    setRatingLoading(true);

    const payload = { token: interviewToken, rating, feedback: feedbackText || null, candidateName: candidateName || null };

    // Try configured API base first, then fall back to relative path
    const candidateBases = [] as string[];
    if (process.env.REACT_APP_API_BASE) candidateBases.push(process.env.REACT_APP_API_BASE.replace(/\/$/, ''));
    // fallback to common localhost default for dev convenience
    candidateBases.push('http://localhost:4000');
    // finally try relative path (useful if the frontend is served by the same host or a proxy)
    candidateBases.push('');

    let lastErr: any = null;
    for (const base of candidateBases) {
      try {
        const url = base ? `${base}/api/rating` : '/api/rating';
        console.log('Submitting rating to', url, 'payload=', payload);
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),

        });
        const data = await resp.json().catch(() => ({}));
        console.log('rating response', resp.status, data);
        if (resp.ok) {
          setRatingSubmitted(true);
          setShowRating(false);
          setStatus('idle');
          setSeconds(0);
          setRatingLoading(false);
          setFeedbackText('');
          return;
        } else {
          lastErr = { status: resp.status, body: data };
          // try next candidate base
        }
      } catch (e) {
        console.warn('rating submit attempt failed for base', base, e);
        lastErr = e;
      }
    }

    // if we reach here all attempts failed
    const errMsg = lastErr && lastErr.status ? `Server responded ${lastErr.status}` : `Network error: ${String(lastErr)}`;
    console.error('All rating submit attempts failed:', lastErr);
    setRatingError(errMsg);
    setRatingLoading(false);
  };
  // If rating UI is active, render that overlay and hide the rest of the UI
  if (showRating) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 20 }}>
        <div style={{ border: '1px solid #e5e7eb', background: '#fff', color: '#111827', padding: '20px', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: 640 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>How would you rate this call?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = (hoverRating ?? selectedRating) !== null ? n <= (hoverRating ?? selectedRating ?? 0) : false;
              return (
                <button
                  key={n}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(null)}
                  onClick={() => { if (!ratingLoading) setSelectedRating(n); }}
                  disabled={ratingLoading}
                  aria-label={`${n} star`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: ratingLoading ? 'default' : 'pointer',
                    padding: 6,
                  }}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill={filled ? '#f59e0b' : 'none'} stroke={filled ? '#f59e0b' : '#9ca3af'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 .587l3.668 7.431L23.4 9.748l-5.7 5.556L19.335 24 12 19.897 4.665 24l1.635-8.696L.6 9.748l7.732-1.73L12 .587z" />
                  </svg>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 12, width: '100%' }}>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Optional — tell us what went well or how we can improve"
              disabled={ratingLoading}
              rows={3}
              aria-label="Feedback about the call"
              style={{ width: '100%', padding: 10, borderRadius: 12, border: '1px solid #e6edf3', resize: 'vertical', fontSize: 14 }}
            />
          </div>
          <div style={{ marginTop: 12, width: '100%', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <button
              onClick={() => {
                // hide rating overlay and reset to ready so call can start again
                setShowRating(false);
                setSelectedRating(null);
                setHoverRating(null);
                setRatingSubmitted(false);
                setRatingLoading(false);
                setRatingError(null);
                setFeedbackText('');
                setStatus('idle');
                setSeconds(0);
                hadActiveCallRef.current = false;
              }}
              type="button"
              disabled={ratingLoading}
              style={{ padding: '8px 12px', background: 'transparent', borderRadius: 8, border: 'none', color: '#6b7280', cursor: ratingLoading ? 'default' : 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={() => { if (!ratingLoading) { if (selectedRating == null) { setRatingError('Please select a star rating first'); return; } submitRating(selectedRating); } }}
              type="button"
              disabled={ratingLoading || selectedRating == null}
              aria-disabled={ratingLoading || selectedRating == null}
              style={{
                padding: '10px 18px',
                background: selectedRating == null ? '#bcd7ff' : '#0A84FF',
                color: '#fff',
                borderRadius: 9999,
                border: 'none',
                boxShadow: selectedRating == null ? 'none' : '0 8px 24px rgba(10,132,255,0.22)',
                fontWeight: 600,
                cursor: (ratingLoading || selectedRating == null) ? 'default' : 'pointer',
                minWidth: 110,
              }}
            >
              {ratingLoading ? (
                <svg width="18" height="18" viewBox="0 0 50 50" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="4" strokeOpacity="0.15" />
                  <path fill="#fff" d="M25 5a20 20 0 0 1 0 40 20 20 0 1 0 0-40" >
                    <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                  </path>
                </svg>
              ) : (
                'Submit'
              )}
            </button>
          </div>
          {ratingError && <div style={{ fontSize: 13, color: '#dc2626' }}>{ratingError}</div>}
        </div>
      </div>
    );
  }

  // When link is blocked or no token present show the warning box; otherwise show the normal UI
  if (linkBlocked || noToken) {
    const title = linkBlocked ? 'Interview link blocked' : 'Invalid interview link';
    const message = linkBlocked
      ? 'This interview link has been opened too many times and is now blocked.'
      : 'This page was not opened via a valid interview link. Please use the interview link you received.';

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 20 }}>
        <div style={{ border: '1px solid #f5c6cb', background: '#fff5f5', color: '#721c24', padding: '16px 20px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, maxWidth: 640 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M11 7h2v6h-2V7z" fill="#721c24" />
            <path d="M11 15h2v2h-2v-2z" fill="#721c24" />
            <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#f5c6cb" />
          </svg>
          <div style={{ fontSize: 14, lineHeight: 1.3 }}>
            <strong style={{ display: 'block', marginBottom: 6 }}>{title}</strong>
            {message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Call Card */}
      <div className="call-card">
        <div className="card-accent" aria-hidden="true"></div>

        {/* Header Section */}
        {status === 'idle' && (
          <div className="interview-header">
            <h1 className="interview-title">
              Welcome{candidateName ? ` ${candidateName}` : ''} <span className="wave-emoji"> 👋</span>, let’s begin your interview
            </h1>

            <p className="interview-subtitle">
              I’ll guide you through a few questions and adjust based on your answers.
              <br />
              Press the <strong>button</strong> when you’re ready.
            </p>
          </div>
        )}

        {/* Header - Timer and Status */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-medium text-slate-700">
              {formatTime(seconds)}
            </div>
          </div>
          <div className="text-right">
            <div
              className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${status === 'live' ? 'status-badge-live' :
                status === 'connecting' ? 'status-badge-connecting' :
                  'status-badge-idle'
                }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${status === 'live' ? 'bg-green-400 pulse' :
                  status === 'connecting' ? 'bg-yellow-400 pulse' :
                    'bg-slate-300'
                  }`}
              ></span>
              <span className="font-medium">
                {status === 'connecting' ? 'Connecting...' :
                  status === 'live' ? 'Live' :
                    status === 'idle' ? 'Ready' :
                      'Ended'}
              </span>
            </div>
          </div>
        </div>

        {/* Main Circle Avatar - Center Stage */}
        <div className="flex flex-col items-center justify-center">
          <div className={`call-circle ${isSpeaking ? 'speaking' : ''} ${status === 'live' ? 'active' : ''}`}>
            <div className="call-circle-inner">
              {/* Animated Wave Bars */}
              <div className="circle-waves">
                <span className="wave-bar"></span>

                <span className="wave-bar"></span>
                <span className="wave-bar"></span>
                <span className="wave-bar"></span>
                <span className="wave-bar"></span>
                <span className="wave-bar"></span>
                <span className="wave-bar"></span>
              </div>
            </div>
          </div>

          {/* Info Text Below Circle */}
          <div className="mt-4 text-center">
            <div className="text-slate-700 font-medium">AI Interviewer</div>
            <div className="text-xs text-slate-400 mt-1">Supply Chain Manager</div>
          </div>

          {/* Controls Container - Always below circle and info */}
          <div className="mt-6 flex items-center justify-center gap-4">
            {/* Start Button - Only visible when idle */}
            {status === 'idle' && (
              <button
                onClick={startVapi}
                className="start-call-btn"
                disabled={linkBlocked}
                title={linkBlocked ? 'Link has been blocked due to too many opens' : 'Start Interview'}
                aria-label="Start Interview"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>
            )}

            {/* Call Controls - Show when connecting or live */}
            {(status === 'connecting' || status === 'live') && (
              <>
                {/* Mic Toggle */}
                <button
                  onClick={toggleMic}
                  className={`control-btn-round ${!micOn ? 'muted' : ''}`}
                  aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}
                  title={micOn ? 'Mute' : 'Unmute'}
                >
                  {micOn ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a2 2 0 0 0-2 2v6a2 2 0 1 0 4 0V4a2 2 0 0 0-2-2z" />
                      <path d="M5 10a5 5 0 0 0 10 0h-1a4 4 0 1 1-8 0H5z" />
                      <path d="M10 15v3" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9V5a3 3 0 10-6 0v4M5 10v1a7 7 0 0014 0v-1M3 3l18 18" />
                    </svg>
                  )}
                </button>

                {/* End Call Button */}
                <button
                  onClick={endCall}
                  className="end-call-btn"
                  aria-label="End call"
                  title="End Interview"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27 11.36 11.36 0 003.48.56 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.56 3.48 1 1 0 01-.27 1.11l-2.17 2.2z" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="footer-text">
          <svg className="w-3.5 h-3.5 inline-block mr-1.5 opacity-50" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          This interview may be recorded for quality purposes
        </div>

        {/* Hidden VAPI Container */}
        <div id="vapiContainer" className="hidden"></div>
      </div>
    </div>
  );
}

(() => {
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await _fetch(...args);
    try {
      const url = (args[0] && args[0].toString()) || '';
      if (url.includes('/call/web')) {
        res.clone().text().then(body => console.log('CALL/WEB response', res.status, body)).catch(() => { });
      }
    } catch (e) { console.warn('fetch-logger error', e); }
    return res;
  };
})();
